import { observable, when, computed, action } from 'mobx';
import { ContainerBlock } from '~/models/blocks/container-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { CompanyInfoLayoutType } from '~/types/builder.types';
import { CompositionType } from '~/types/model.types';
import { ICompanyInfoBlockResponse } from '~/types/server.types';
import { setCompanyInfoOnDescendants } from '~/utils/block.utils';

type CompanyInfoBlockDataForServer = ReturnType<
    ContainerBlock['getDataForServer']
> &
    Pick<
        ICompanyInfoBlockResponse,
        | 'composition_type'
        | 'company_info_layout_type'
        | 'linked_to_company_settings'
        | 'type'
    >;

export class CompanyInfoBlock
    extends ContainerBlock
    implements ICompanyInfoBlockResponse
{
    type: 'company_info_block';
    @observable composition_type: CompositionType.CompanyInfo;
    @observable company_info_layout_type: CompanyInfoLayoutType;
    @observable linked_to_company_settings: boolean = true;

    constructor(
        block: ICompanyInfoBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;

        when(
            () => !!this.flow,
            () => setCompanyInfoOnDescendants(this)
        );
    }

    @action
    mergeBlockAttributes(block: ICompanyInfoBlockResponse, attributes) {
        super.mergeBlockAttributes(block, attributes);
        this.linked_to_company_settings = block.linked_to_company_settings;
    }

    get isPrintable() {
        return !this.page.isPaymentPage;
    }

    get isDeletable() {
        return true;
    }

    @computed
    get isLinkedInTemplate() {
        return (
            this.linked_to_company_settings &&
            (this.flow?.is_template || this.flow?.isLeadForm)
        );
    }

    @computed
    get isChangeColumnsDisabled() {
        return this.isLinkedInTemplate;
    }

    getDataForServer(): CompanyInfoBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type,
            composition_type: this.composition_type,
            company_info_layout_type: this.company_info_layout_type,
            linked_to_company_settings: this.linked_to_company_settings
        };
    }
}
